import { useTranslation } from "react-i18next";
import { useSupplierPayables } from "@/api/kpis";
import { formatMoney } from "@/domain/money";
import { LoadingState, ErrorState } from "@/components/ui/States";
import { Button } from "@/components/ui/Button";
import { Printer } from "lucide-react";
import { printSupplierStatement } from "@/lib/printSupplierStatement";

/**
 * Bảng kê "Hisense đang nợ tôi bao nhiêu" mà nhà cung cấp tự xem.
 *
 * Từ 17/09/2026 có cả PHIẾU GHI NỢ đã ghi sổ (khoản phạt từ khiếu nại nhà cung cấp): phần đã trừ vào
 * hoá đơn nằm ở cột "trừ bằng phiếu ghi nợ" của hoá đơn đó, phần chưa trừ là dòng của phiếu với số dư âm.
 *
 * Khách chốt 14/09/2026 (P7): nhà cung cấp tự đăng nhập và CHỈ xem được 3
 * tháng. Luật cửa sổ nằm ở `supplierStatement-logic.ts` trên máy chủ; ở đây
 * chỉ là hiển thị — kể cả tổng, cũng do máy chủ tính, vì cộng lại trên trình
 * duyệt là tạo cơ hội cho hai con số khác nhau của cùng một khoản nợ.
 *
 * Tổng TÁCH THEO LOẠI TIỀN và không bao giờ quy đổi: hệ không giữ tỷ giá ở bất
 * cứ đâu, nên một dòng "tổng cộng" gộp USD với CNY là con số không có thật.
 */
export function SupplierPayablesPanel({ supplierId }: { supplierId: string }) {
  const { t } = useTranslation();
  const query = useSupplierPayables(supplierId);
  if (!supplierId) return null;

  const data = query.data?.data;
  return (
    <section className="mt-6">
      <div className="mb-1 flex items-end justify-between gap-2 border-b border-line pb-1.5">
        <h2 className="eyebrow text-ink-faint">{t("portal.payables")}</h2>
        {/* Bản in dựng từ đúng dữ liệu máy chủ đã trả cho bảng này — không cộng lại gì.
            Hiện cả khi không còn nợ: bảng kê "không có gì phải trả" cũng là một tài liệu để gửi. */}
        {data ? (
          <Button size="sm" variant="secondary" onClick={() => printSupplierStatement(data)}>
            <Printer className="size-3.5" aria-hidden />
            {t("portal.printStatement")}
          </Button>
        ) : null}
      </div>
      {query.isLoading ? (
        <LoadingState />
      ) : query.error ? (
        <ErrorState message={t("portal.payablesError")} onRetry={() => void query.refetch()} />
      ) : !data ? null : (
        <>
          <p className="mb-3 mt-2 text-xs text-ink-soft">
            {t("portal.payablesIntro", { from: data.windowStart })}
          </p>
          {data.lines.length === 0 ? (
            <p className="text-sm text-ink-soft">{t("portal.payablesNone")}</p>
          ) : (
            <>
              <div className="overflow-x-auto rounded border border-line">
                <table className="w-full min-w-[760px] text-sm">
                  <thead className="bg-sunken">
                    <tr>
                      <th className="p-3 text-left">{t("portal.payableDate")}</th>
                      <th className="p-3 text-left">{t("portal.payableNo")}</th>
                      <th className="p-3 text-right">{t("portal.payableTotal")}</th>
                      <th className="p-3 text-right">{t("portal.payablePaid")}</th>
                      <th className="p-3 text-right">{t("portal.payableDeducted")}</th>
                      <th className="p-3 text-right">{t("portal.payableOutstanding")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.lines.map((l) => {
                      const debitNote = l.kind === "DebitNote";
                      return (
                        <tr key={l.documentId} className="border-t border-line">
                          <td className="p-3 font-mono tabular">{l.documentDate}</td>
                          <td className="p-3 font-mono">
                            {l.documentNo}
                            {/* Phiếu ghi nợ: khoản trừ từ khiếu nại nhà cung cấp — ghi rõ loại và căn cứ. */}
                            {debitNote && (
                              <span className="ml-2 rounded bg-info-tint px-1.5 py-0.5 font-sans text-2xs text-info">{t("portal.payableDebitNote")}</span>
                            )}
                            {/*
                              Chứng từ ngoài cửa sổ 3 tháng vẫn hiện vì còn số dư.
                              Không đánh dấu thì nhà cung cấp tưởng cửa sổ tính sai.
                            */}
                            {l.carriedOver && (
                              <span className="ml-2 rounded bg-warn-tint px-1.5 py-0.5 font-sans text-2xs text-warn">
                                {t("portal.payableCarried")}
                              </span>
                            )}
                            {debitNote && l.reference && <span className="block font-sans text-xs text-ink-soft">{t("portal.payableClaim", { no: l.reference })}</span>}
                          </td>
                          {/* Số tiền phiếu ghi nợ hiện dấu âm: nó làm giảm số Hisense phải trả. */}
                          <td className="p-3 text-right font-mono tabular">{formatMoney(debitNote ? -l.amountMinor : l.amountMinor, l.currency)}</td>
                          <td className="p-3 text-right font-mono tabular">{debitNote ? "—" : formatMoney(l.paidMinor, l.currency)}</td>
                          <td className="p-3 text-right font-mono tabular">{formatMoney(l.deductedMinor, l.currency)}</td>
                          <td className="p-3 text-right font-mono tabular">{formatMoney(l.balanceMinor, l.currency)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {data.totals.length > 0 && (
                <p className="mt-3 text-sm">
                  {t("portal.payablesTotal")}:
                  {data.totals.map((x) => (
                    <span key={x.currency} className="ml-2 font-mono font-medium tabular">
                      {formatMoney(x.balanceMinor, x.currency)}
                    </span>
                  ))}
                </p>
              )}
              {(data.totals.some((x) => x.balanceMinor < 0) || data.lines.some((l) => l.balanceMinor < 0)) && (
                <p className="mt-1 text-xs text-ink-soft">{t("portal.payablesNegative")}</p>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
}
